// Factory function pattern to avoid Webpack TDZ issues with class inheritance
// This uses BaseCustomerFactory instead of class inheritance

import { createBaseCustomer, type IBaseCustomer } from "../BaseCustomerFactory";
import type DocumentNotary from "le-coffre-resources/dist/Notary/DocumentNotary";
import type { QueryPayload } from "../../../types";
import type { IGetDocumentNotaryparams } from "./DocumentsNotary";
import { buildEmptyPayload } from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";
import { prepareGetByUidQueryForRequest } from "@Front/Api/helpers/getByUidClientOptionsHelpers";

interface DocumentsNotaryInstance {
	get(q: IGetDocumentNotaryparams): Promise<DocumentNotary[]>;
	getByUid(uid: string, q?: QueryPayload): Promise<DocumentNotary>;
	markAsViewed(uid: string): Promise<DocumentNotary>;
}

// Module-level singleton instance
let instance: DocumentsNotaryInstance | null = null;
let baseCustomerInstance: IBaseCustomer | null = null;

function createDocumentsNotaryGetByUid(baseCustomer: IBaseCustomer, baseUrl: string) {
	return async (uid: string, q?: QueryPayload): Promise<DocumentNotary> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		const { requestQuery, suppressUserFacingErrorOnFailure } = prepareGetByUidQueryForRequest(q);
		if (requestQuery) baseCustomer.appendJsonSearchParams(url, { q: requestQuery });
		try {
			return await baseCustomer.getRequest<DocumentNotary>(url);
		} catch (err) {
			if (!suppressUserFacingErrorOnFailure) {
				baseCustomer.onError(err);
			}
			return Promise.reject(err);
		}
	};
}

function createDocumentsNotary(): DocumentsNotaryInstance {
	if (!baseCustomerInstance) {
		baseCustomerInstance = createBaseCustomer();
	}

	const baseCustomer = baseCustomerInstance;
	const baseUrl = baseCustomer.namespaceUrl.concat("/documents_notary");

	return {
		get: async (q: IGetDocumentNotaryparams): Promise<DocumentNotary[]> => {
			const url = new URL(baseUrl);
			baseCustomer.appendJsonSearchParams(url, { q });
			try {
				return await baseCustomer.getRequest<DocumentNotary[]>(url);
			} catch (err) {
				baseCustomer.onError(err);
				return Promise.reject(err);
			}
		},
		getByUid: createDocumentsNotaryGetByUid(baseCustomer, baseUrl),
		markAsViewed: async (uid: string): Promise<DocumentNotary> => {
			const url = new URL(baseUrl.concat(`/${uid}/mark-as-viewed`));
			try {
				return await baseCustomer.putRequest<DocumentNotary>(url, buildEmptyPayload());
			} catch (err) {
				baseCustomer.onError(err);
				return Promise.reject(err);
			}
		},
	};
}

export function getDocumentsNotaryInstance(): DocumentsNotaryInstance {
	if (!instance) {
		instance = createDocumentsNotary();
	}
	return instance;
}
